const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const labelForKey = (key) => key
  .replace(/([A-Z])/g, ' $1')
  .replace(/_/g, ' ')
  .replace(/^./, (char) => char.toUpperCase());

const renderValue = (key, value) => {
  if (!value) return '<span class="muted">-</span>';
  if (key.toLowerCase().includes('signature') && typeof value === 'string' && value.startsWith('data:image/')) {
    return `<img class="signature" src="${value}" alt="${escapeHtml(labelForKey(key))}" />`;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.map((item) => escapeHtml(item)).join(', ');
  }
  if (typeof value === 'object') {
    return `<pre>${escapeHtml(JSON.stringify(value, null, 2))}</pre>`;
  }
  return escapeHtml(value);
};

export const downloadSignedFormCopy = ({
  formTitle,
  employeeName,
  submittedAt,
  language = 'en',
  formData = {},
  signatureData
}) => {
  const title = formTitle || (language === 'es' ? 'Formulario Firmado' : 'Signed Form');
  const submittedLabel = language === 'es' ? 'Enviado' : 'Submitted';
  const employeeLabel = language === 'es' ? 'Empleado' : 'Employee';
  const fields = Object.entries(formData || {})
    .filter(([key]) => key !== 'signature_data')
    .map(([key, value]) => `
      <tr>
        <th>${escapeHtml(labelForKey(key))}</th>
        <td>${renderValue(key, value)}</td>
      </tr>
    `)
    .join('');

  const html = `<!doctype html>
<html lang="${language === 'es' ? 'es' : 'en'}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #1f2933; margin: 32px; }
    h1 { margin-bottom: 4px; color: #000062; }
    .meta { color: #52606d; margin-bottom: 24px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #d9e2ec; padding: 10px; vertical-align: top; text-align: left; }
    th { width: 32%; background: #f5f7fa; }
    .signature { max-width: 360px; max-height: 120px; border: 1px solid #d9e2ec; background: #fff; }
    .muted { color: #829ab1; }
    pre { white-space: pre-wrap; margin: 0; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">
    <div><strong>${employeeLabel}:</strong> ${escapeHtml(employeeName || formData.employeeName || formData.name || '-')}</div>
    <div><strong>${submittedLabel}:</strong> ${submittedAt ? escapeHtml(new Date(submittedAt).toLocaleString()) : '-'}</div>
  </div>
  <table>
    <tbody>
      ${fields}
      ${signatureData ? `
      <tr>
        <th>Primary Signature</th>
        <td>${renderValue('primarySignature', signatureData)}</td>
      </tr>` : ''}
    </tbody>
  </table>
</body>
</html>`;

  // TKT-199: Naomi reported the download produced a raw .html file
  // (useful only if you reopen it in a browser) instead of something
  // the office could store or email as a PDF. Open the rendered form
  // in a new window/tab with an auto-triggered print dialog so the
  // user can "Save as PDF" through the browser's native print flow.
  // This works on desktop Chrome/Edge/Safari and on iPad Safari, which
  // is what the field staff actually use. Download-as-HTML stays
  // available as a fallback when popups are blocked.
  const htmlWithPrintHook = html.replace(
    '</body>',
    `<script>
       try {
         window.addEventListener('load', function () {
           setTimeout(function () { window.print(); }, 250);
         });
       } catch (e) { /* no-op */ }
     </script>
     </body>`
  );

  const printWindow = window.open('', '_blank', 'noopener,noreferrer');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(htmlWithPrintHook);
    printWindow.document.close();
    return;
  }

  // Popup blocked — fall back to the legacy HTML download so the user
  // at least gets the data instead of a silent no-op.
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const safeTitle = title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'signed-form';
  const link = document.createElement('a');
  link.href = url;
  link.download = `${safeTitle}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
